import apiService from './apiService';
import { getApiBaseUrl } from '../config/apiConfig';

const PREFIX = '/secteurs/admin';
const ENTREPRISES_PRODUITS_TIMEOUT = 120000;

async function request(promise) {
  const res = await promise;
  if (!res.success) {
    throw new Error(res.message || 'Erreur API');
  }
  return res;
}

const secteursAdminService = {
  // --- Secteurs ---
  async listSecteurs({ search = '', page = 1, limit = 20 } = {}) {
    const res = await request(apiService.get(`${PREFIX}/secteurs`, { search, page, limit }));
    return { secteurs: res.data.secteurs ?? [], pagination: res.pagination ?? {} };
  },

  async getSecteur(id) {
    const res = await request(apiService.get(`${PREFIX}/secteurs/${id}`));
    return res.data.secteur ?? null;
  },

  async createSecteur(data) {
    const res = await request(apiService.post(`${PREFIX}/secteurs`, data));
    return res.data.secteur;
  },

  async updateSecteur(id, data) {
    const res = await request(apiService.put(`${PREFIX}/secteurs/${id}`, data));
    return res.data.secteur;
  },

  async deleteSecteur(id) {
    await request(apiService.delete(`${PREFIX}/secteurs/${id}`));
  },

  // --- Zones ---
  async listZones({ search = '', page = 1, limit = 20 } = {}) {
    const res = await request(apiService.get(`${PREFIX}/zones`, { search, page, limit }));
    return { zones: res.data.zones ?? [], pagination: res.pagination ?? {} };
  },

  async getZone(id) {
    const res = await request(apiService.get(`${PREFIX}/zones/${id}`));
    return res.data.zone ?? null;
  },

  async updateZone(id, data) {
    const res = await request(apiService.put(`${PREFIX}/zones/${id}`, data));
    return res.data.zone;
  },

  async updateZonePolygon(id, polygon, centre) {
    await request(apiService.put(`${PREFIX}/zones/${id}/polygon`, { polygon, centre }));
  },

  // --- Commerciaux ---
  async listCommerciaux({ search = '', active = '', page = 1, limit = 20 } = {}) {
    const res = await request(apiService.get(`${PREFIX}/commerciaux`, {
      search,
      active: active === '' ? undefined : active,
      page,
      limit,
    }));
    return { commerciaux: res.data.commerciaux ?? [], pagination: res.pagination ?? {} };
  },

  async getCommercial(id) {
    const res = await request(apiService.get(`${PREFIX}/commerciaux/${id}`));
    return res.data.commercial ?? null;
  },

  async createCommercial(data) {
    const res = await request(apiService.post(`${PREFIX}/commerciaux`, data));
    return res.data.commercial;
  },

  async updateCommercial(id, data) {
    const res = await request(apiService.put(`${PREFIX}/commerciaux/${id}`, data));
    return res.data.commercial;
  },

  async deleteCommercial(id) {
    await request(apiService.delete(`${PREFIX}/commerciaux/${id}`));
  },

  getCommercialPhotoUrl(id) {
    return `${getApiBaseUrl()}${PREFIX}/commerciaux/${id}/photo`;
  },

  async fetchCommercialPhotoBlob(id) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    const response = await fetch(this.getCommercialPhotoUrl(id), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      return null;
    }
    return response.blob();
  },

  async uploadCommercialPhoto(id, file) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${getApiBaseUrl()}${PREFIX}/commerciaux/${id}/photo`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      throw new Error(data.message || `Erreur upload photo (HTTP ${response.status})`);
    }

    return data.data?.commercial ?? null;
  },

  async deleteCommercialPhoto(id) {
    const res = await request(apiService.delete(`${PREFIX}/commerciaux/${id}/photo`));
    return res.data?.commercial ?? null;
  },

  async listUsersDisponibles({ search = '', excludeCommercialId = null, limit = 20 } = {}) {
    const res = await request(apiService.get(`${PREFIX}/commerciaux/users-disponibles`, {
      search: search || undefined,
      exclude_commercial_id: excludeCommercialId || undefined,
      limit,
    }));
    return res.data.users ?? [];
  },

  // --- Entreprises HubSpot ---
  async listEntreprises({ search = '', page = 1, limit = 20 } = {}) {
    const res = await request(apiService.get(`${PREFIX}/entreprises`, { search, page, limit }));
    return { entreprises: res.data.entreprises ?? [], pagination: res.pagination ?? {} };
  },

  async getEntreprise(id) {
    const res = await request(apiService.get(`${PREFIX}/entreprises/${id}`));
    return res.data.entreprise ?? null;
  },

  async createEntreprise(data) {
    const res = await request(apiService.post(`${PREFIX}/entreprises`, data));
    return res.data.entreprise;
  },

  async updateEntreprise(id, data) {
    const res = await request(apiService.put(`${PREFIX}/entreprises/${id}`, data));
    return res.data.entreprise;
  },

  async deleteEntreprise(id) {
    await request(apiService.delete(`${PREFIX}/entreprises/${id}`));
  },

  // --- Produits société ---
  async listProduitsSociete({ search = '', page = 1, limit = 20 } = {}) {
    const res = await request(apiService.get(`${PREFIX}/produits-societe`, { search, page, limit }));
    return {
      produits: res.data.produits_societe ?? [],
      pagination: res.pagination ?? {},
      produitsNonMappes: res.pagination?.produits_non_mappes ?? [],
    };
  },

  async getProduitSociete(id) {
    const res = await request(apiService.get(`${PREFIX}/produits-societe/${id}`));
    return res.data.produit_societe ?? null;
  },

  async createProduitSociete(data) {
    const res = await request(apiService.post(`${PREFIX}/produits-societe`, data));
    return res.data.produit_societe;
  },

  async updateProduitSociete(id, data) {
    const res = await request(apiService.put(`${PREFIX}/produits-societe/${id}`, data));
    return res.data.produit_societe;
  },

  async deleteProduitSociete(id) {
    await request(apiService.delete(`${PREFIX}/produits-societe/${id}`));
  },

  async getFamillesProduits() {
    const res = await request(apiService.get(`${PREFIX}/produits-societe/familles`));
    return res.data.familles ?? [];
  },

  // --- Statistiques ---
  async getProduits(annee) {
    const res = await request(apiService.get(`${PREFIX}/produits`, { annee }));
    return res.data;
  },

  async getCommandes(limit = 50) {
    const res = await request(apiService.get(`${PREFIX}/commandes`, { limit }));
    return {
      commandes: res.data.commandes ?? [],
      pagination: res.pagination ?? {},
    };
  },

  async getProduitsCommerciaux(annee) {
    const res = await request(apiService.get(`${PREFIX}/produits/commerciaux`, { annee }));
    return res.data;
  },

  async getEntreprisesProduitsFiltres(annee) {
    try {
      const res = await request(apiService.get(
        `${PREFIX}/entreprises-produits/filtres`,
        { annee },
        { timeout: ENTREPRISES_PRODUITS_TIMEOUT }
      ));
      return res.data;
    } catch (err) {
      // API pas encore déployée avec /filtres — les filtres viendront de getEntreprisesProduits()
      if (String(err.message || '').includes('404')) {
        return null;
      }
      throw err;
    }
  },

  async getEntreprisesProduits({ annee, produit, societe, vendeur, client, includeFiltres = false } = {}) {
    const params = { annee, produit, societe, vendeur, client };
    if (includeFiltres) params.include_filtres = 1;

    const res = await request(apiService.get(
      `${PREFIX}/entreprises-produits`,
      params,
      { timeout: ENTREPRISES_PRODUITS_TIMEOUT }
    ));
    return res.data;
  },
};

export default secteursAdminService;
